import { Component, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LoadingBarService } from '@ngx-loading-bar/core';
import Swal from 'sweetalert2';
import { TransferDataService } from '../../../transfers/services/transfer-data.service';

@Component({
  selector: 'kt-transfer-out-confirmation-dialog',
  templateUrl: './transfer-out-confirmation-dialog.component.html',
  styleUrls: ['./transfer-out-confirmation-dialog.component.scss']
})
export class TransferOutConfirmationDialogComponent implements OnDestroy {
  loading = false;
  subscription = new Subscription();

  gameProviderName: string;
  gameWalletBalance: number;
  transferOutData: any;
  selectedTransferTo: any = null;

  constructor(
    public dialogRef: MatDialogRef<TransferOutConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
    private loadingBar: LoadingBarService,
    private transferDataService: TransferDataService
  ) {
    this.gameProviderName = data?.gameProviderName || '';
    this.gameWalletBalance = data?.gameWalletBalance || 0;
    this.transferOutData = data?.transferOutData || {};
      this.selectedTransferTo = data?.selectedTransferTo || null;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onNo(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onYes(): void {
    this.loading = true;
    this.loadingBar.start();

    if (this.gameWalletBalance > 0) {
      const transferOutPayload = {
        member_account_id: this.transferOutData?.member_account_id,
        game_provider_id: this.transferOutData?.game_provider_id,
        transfer_type: 2,
        amount: this.transferOutData?.game_wallet_balance || this.gameWalletBalance,
        promotion_id: 0
      };

      this.subscription.add(
        this.transferDataService.add(transferOutPayload as any, true, true).subscribe(
          (transferResponse) => {
            this.finishLoadingBar(false);

            if (transferResponse?.success) {
                // After clearing target provider balance, if the original request selected a game provider
                // as transfer_from, perform transfer-out from that provider to Main Wallet first.
                const fromProviderId = this.transferOutData?.transfer_from;
                const fromAmount = this.transferOutData?.amount;

                const isFromGameProvider = fromProviderId !== undefined && fromProviderId !== null && Number(fromProviderId) !== 0 && Number(fromAmount) > 0;

                if (isFromGameProvider) {
                  const transferOutFromPayload = {
                    member_account_id: this.transferOutData?.member_account_id,
                    game_provider_id: fromProviderId,
                    transfer_type: 2,
                    amount: fromAmount,
                    promotion_id: 0
                  };

                  this.subscription.add(
                    this.transferDataService.add(transferOutFromPayload as any, true, true).subscribe(
                      (transferFromResp: any) => {
                        if (transferFromResp?.success) {
                          this.executeClaimTransfer(this.transferOutData, true);
                          return;
                        }

                        this.loading = false;
                        this.showSwal('Transfer Failed', transferFromResp?.message || 'Failed to transfer out from selected source wallet');
                        this.cdr.detectChanges();
                      },
                      (error) => {
                        this.loading = false;
                        this.showSwal('Transfer Error', error?.error?.message || error?.message || 'Failed to transfer out selected source wallet. Please try again.');
                        this.cdr.detectChanges();
                      }
                    )
                  );
                  return;
                }

                // No source transfer needed, proceed to claim
                this.executeClaimTransfer(this.transferOutData, true);
              return;
            }

            this.loading = false;
            this.showSwal('Transfer Failed', transferResponse?.message || 'Failed to transfer out wallet balance');
            this.cdr.detectChanges();
          },
          (error) => {
            this.finishLoadingBar(false);
            this.loading = false;
            this.showSwal('Transfer Error', error?.error?.message || error?.message || 'Failed to transfer wallet balance. Please try again.');
            this.cdr.detectChanges();
          }
        )
      );
    } else {
      this.loadingBar.stop();
      // directly claim when no balance to transfer
      this.executeClaimTransfer(this.transferOutData);
    }
  }

  private executeClaimTransfer(data: any, completeLoadingBar: boolean = false): void {
    this.loading = true;
    this.loadingBar.start();
    // Build claim payload similar to ClaimRewardDialogComponent.executeTransfer logic
    const toId = data?.game_provider_id ?? data?.transfer_to ?? this.transferOutData?.transfer_to ?? this.selectedTransferTo?.game_provider_id ?? this.selectedTransferTo?.id;

    const claimPayload: any = { ...data };
    if (toId !== undefined && toId !== null) {
      claimPayload.transfer_to = toId;
      claimPayload.game_provider_id = toId;
    }
    // Ensure transfer_in comes from Main Wallet
    claimPayload.transfer_from = 0;

    this.subscription.add(
      this.transferDataService.add(claimPayload).subscribe(
        (response: any) => {
          this.finishLoadingBar(!!completeLoadingBar);
          this.loading = false;

          if (response) {
            Swal.fire({ title: 'Success!', html: 'Reward claimed successfully!', icon: 'success', confirmButtonColor: '#3085d6', confirmButtonText: 'OK' })
            .then(() => this.dialogRef.close({ action: 'success', data: response }));
          } else {
            this.showSwal('Error!5', 'Failed to claim reward');
          }
          this.cdr.detectChanges();
        },
        (error) => {
          this.finishLoadingBar(!!completeLoadingBar);
          this.loading = false;
          this.showSwal('Error!6', error?.error?.message || error?.message || 'Failed to claim reward');
          this.cdr.detectChanges();
        }
      )
    );
  }

  private formatToHtml(input: any): string {
    if (Array.isArray(input)) return input.join('<br>');
    return String(input || '');
  }

  private finishLoadingBar(complete: boolean): void {
    if (complete) {
      this.loadingBar.complete();
    } else {
      this.loadingBar.stop();
    }
  }

  private showSwal(title: string, message: any, icon: 'error' | 'success' | 'info' = 'error', confirmButtonText: string = 'OK'): void {
    Swal.fire({
      title,
      html: this.formatToHtml(message),
      icon,
      confirmButtonColor: '#3085d6',
      confirmButtonText
    });
  }
}
